/**
 * What a bulk invite send will actually do, worked out before the host clicks.
 *
 * The send button used to read "Send N invites" where N was the roster size, and
 * fired on one click with no confirmation. On a roster where almost every row
 * has no email and no phone that label is a promise the API cannot keep: it
 * skips, in order, guests with no contact details, then suppressed
 * (unsubscribed) guests, then declined guests. Only the first and third are
 * visible from the browser, so `reachable` is an upper bound — the UI has to say
 * so rather than imply an exact number.
 */
import type { GroupInvitee } from '../types/groups';

export interface InviteReachability {
  total: number;
  /** Skipped first by the API: nothing to send to. */
  noContact: number;
  /** Skipped last by the API: has contact details but said no. */
  declined: number;
  /** Upper bound on how many messages go out; suppression can lower it further. */
  reachable: number;
}

export function summarizeReachability(invitees: GroupInvitee[]): InviteReachability {
  const withContact = invitees.filter((invitee) => invitee.email || invitee.phone);
  const declined = withContact.filter((invitee) => invitee.rsvpStatus === 'declined');
  return {
    total: invitees.length,
    noContact: invitees.length - withContact.length,
    declined: declined.length,
    reachable: withContact.length - declined.length,
  };
}
